import React, { useEffect, useRef } from "react"
import StorageService from "@/services/Storage"
import useStyles from "@/components/Messages/MediaMessage/AudioPlayer/styles"
import { useGlobalStateStore } from "@/store/GlobalState"

type AudioPlayerProps = {
	src: string
	type?: string
}

const STORAGE_KEY_BASE = "inbox:audio-playback-rate"

const AudioPlayer: React.FC<AudioPlayerProps> = (props) => {
	const { src, type } = props
	const defaultType = "audio/mpeg"
	const classes = useStyles()
	const audioRef = useRef<HTMLAudioElement | null>(null)
	const globalStateStore = useGlobalStateStore()

	const getPlaybackRateStorageKey = (): string | null => {
		const identifier = `channel:${globalStateStore.currentChannel?.id}-user:${globalStateStore.user.id}`

		return StorageService.mountKey(STORAGE_KEY_BASE, identifier)
	}

	const applySavedPlaybackRate = (audio: HTMLAudioElement, key: string) => {
		const savedRate = StorageService.get<number>(key)
		if (savedRate) {
			audio.playbackRate = savedRate
		}
	}

	const registerRateChangeListener = (audio: HTMLAudioElement, key: string) => {
		const handler = () => {
			StorageService.set(key, audio.playbackRate)
		}

		audio.addEventListener("ratechange", handler)

		return () => {
			audio.removeEventListener("ratechange", handler)
		}
	}

	useEffect(() => {
		const audio = audioRef.current
		const key = getPlaybackRateStorageKey()

		if (!audio || !key) {
			return
		}

		applySavedPlaybackRate(audio, key)
		const cleanup = registerRateChangeListener(audio, key)

		return cleanup
	}, [])

	return (
		<audio
			ref={audioRef}
			controls
			className={classes.audioPlayer}
		>
			<source src={src} type={type || defaultType} />
		</audio>
	)
}

export default AudioPlayer
